const FLY_MS = 400;
const CONTENT_FADE_MS = 10;
const PULSE_MS = 650;
const PULSE_COUNT = 3;

const play = (el: HTMLElement, keyframes: Keyframe[], options: KeyframeAnimationOptions) =>
	new Promise<void>((resolve) => {
		const animation = el.animate(keyframes, options);
		animation.onfinish = () => resolve();
		animation.oncancel = () => resolve();
	});

// The popup itself unmounts on close, so a clone of it does the flying.
const fly = (from: Element, toRect: DOMRect) => {
	const rect = from.getBoundingClientRect();
	if (!rect.width || !rect.height) return Promise.resolve();

	const clone = from.cloneNode(true) as HTMLElement;
	Object.assign(clone.style, {
		position: 'fixed',
		left: `${rect.left}px`,
		top: `${rect.top}px`,
		width: `${rect.width}px`,
		height: `${rect.height}px`,
		margin: '0',
		zIndex: '1400',
		pointerEvents: 'none',
		transformOrigin: 'top left',
	});
	document.body.appendChild(clone);

	try {
		// Shrunk content looks messy — fade it out first, only the empty card flies.
		Array.from(clone.children).forEach((child) => (child as HTMLElement).animate(
			[{ opacity: 1 }, { opacity: 0 }],
			{ duration: CONTENT_FADE_MS, fill: 'forwards' },
		));
	} catch (e) {
		clone.remove();
		throw e;
	}

	const scale = toRect.width / rect.width;
	const x = toRect.left + toRect.width / 2 - rect.width * scale / 2 - rect.left;
	const y = toRect.top + toRect.height / 2 - rect.height * scale / 2 - rect.top;
	return play(clone, [
		{ transform: 'none', opacity: 1 },
		{ transform: `translate(${x}px, ${y}px) scale(${scale})`, opacity: .2 },
	], { duration: FLY_MS, easing: 'ease-in' })
		.finally(() => clone.remove());
};

const pulse = (el: HTMLElement) => play(el, [
	{ transform: 'scale(1)', filter: 'brightness(1)' },
	{ transform: 'scale(1.1)', filter: 'brightness(.8)' },
	{ transform: 'scale(1)', filter: 'brightness(1)' },
], { duration: PULSE_MS, iterations: PULSE_COUNT });

// Shrinks the closing popup into the button that reopens it, then pulses that button.
// Resolves once the flight is over (the pulse plays on its own) and never rejects.
export const flyToButton = async (from: Element | null, to: HTMLElement | null): Promise<void> => {
	if (!to) return;
	if (window.matchMedia?.('(prefers-reduced-motion: reduce)').matches) {
		// No motion, but still hint where the popup went: one brightness blink.
		play(to, [
			{ filter: 'brightness(1)' },
			{ filter: 'brightness(.8)' },
			{ filter: 'brightness(1)' },
		], { duration: PULSE_MS }).catch(() => { });
		return;
	}
	try {
		if (from) await fly(from, to.getBoundingClientRect());
	} catch (e) {
		// No flight then — still pulse the button.
	}
	pulse(to).catch(() => { });
};
